import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../components/Toast/toast.ts";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  ArrowLeft,
  Lock,
  ShieldCheck,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import { PasswordField } from "../../components/PasswordField/index.ts";
import PasswordStrengthBar from "../../components/PasswordField/PasswordStrengthBar.tsx";
import AppButton from "../../components/ui/AppButton.tsx";

interface CheckItem {
  label: string;
  test: (p: string) => boolean;
}

const checks: CheckItem[] = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  {
    label: "Mix of upper & lowercase",
    test: (p) => /[A-Z]/.test(p) && /[a-z]/.test(p),
  },
  { label: "Contains a number", test: (p) => /[0-9]/.test(p) },
  { label: "Contains a symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
  { label: "12+ characters for extra strength", test: (p) => p.length >= 12 },
];

const ChangePassword: React.FC = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChangePassword = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }

    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setLoading(true);
      axios
        .post(
          `${process.env.REACT_APP_API_URL}/api/cust-portal/customer/change-password`,
          { existingPassword: oldPassword, newPassword },
          { headers: { Authorization: `Bearer ${storedToken}` } },
        )
        .then((response) => {
          if (response?.status === 200) {
            setLoading(false);
            toast.success("Password updated successfully!");
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
          }
        })
        .catch((error) => {
          setLoading(false);
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              "An error occurred.",
          );
        });
    }
  };

  return (
    <div className="flex flex-col gap-6">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ─── Form Card ─── */}
        <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-[rgb(var(--border-primary))]">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-md hover:bg-[rgb(var(--surface-2))] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gold-2/10 flex items-center justify-center">
                <Lock size={13} className="text-gold-2" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-[rgb(var(--text-primary))] leading-tight">
                  Secure update
                </p>
                <p className="text-[10px] text-[rgb(var(--text-muted))]">
                  Encrypted in transit · never stored in plain text
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-10">
                <ProgressSpinner
                  style={{ width: "50px", height: "50px" }}
                  strokeWidth="3"
                  animationDuration=".5s"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <PasswordField
                  id="old-password"
                  label="Current Password"
                  value={oldPassword}
                  onChange={setOldPassword}
                  placeholder="Enter your current password"
                />

                <div className="flex flex-col gap-2">
                  <PasswordField
                    id="new-password"
                    label="New Password"
                    value={newPassword}
                    onChange={setNewPassword}
                    placeholder="Enter new password"
                  />
                  <PasswordStrengthBar password={newPassword} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <PasswordField
                    id="confirm-password"
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    placeholder="Type it once more"
                  />
                  {confirmPassword && (
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={
                          newPassword === confirmPassword ? "match" : "nomatch"
                        }
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.2 }}
                        className={`text-[11px] font-medium ${newPassword === confirmPassword ? "text-[rgb(var(--success))]" : "text-[rgb(var(--danger))]"}`}
                      >
                        {newPassword === confirmPassword
                          ? "✓ Passwords match"
                          : "✗ Passwords do not match"}
                      </motion.p>
                    </AnimatePresence>
                  )}
                </div>

                <AppButton
                  onClick={handleChangePassword}
                  appVariant="primary"
                  loading={loading}
                  sx={{
                    width: "100%",
                    height: "42px",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  <div className="flex items-center gap-3" >
                    <div>
                      <Lock size={14} />
                    </div>
                    Update password
                  </div>
                </AppButton>
              </div>
            )}
          </div>
        </div>

        {/* ─── Live Strength Checklist ─── */}
        <div className="border border-[rgb(var(--border-primary))] rounded-lg bg-[rgb(var(--surface-1))] overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-[rgb(var(--border-primary))]">
            <div className="w-7 h-7 rounded-full bg-gold-2/10 flex items-center justify-center">
              <ShieldCheck size={13} className="text-gold-2" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-[rgb(var(--text-primary))] leading-tight">
                Live strength check
              </p>
              <p className="text-[10px] text-[rgb(var(--text-muted))]">
                Each item lights up as you type
              </p>
            </div>
          </div>

          {/* Checklist */}
          <div className="p-4 flex flex-col gap-2">
            {checks.map((check, i) => {
              const passed = newPassword ? check.test(newPassword) : false;
              return (
                <motion.div
                  key={check.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: i * 0.06,
                    duration: 0.3,
                    ease: "easeOut",
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-400 ${
                    passed
                      ? "bg-[rgb(var(--success)/0.08)] border border-[rgb(var(--success)/0.2)]"
                      : "bg-[rgb(var(--surface-2))] border border-transparent"
                  }`}
                >
                  <AnimatePresence mode="wait">
                    {passed ? (
                      <motion.span
                        key="check"
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0, rotate: 90 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        }}
                      >
                        <CheckCircle2
                          size={16}
                          className="text-[rgb(var(--success))] shrink-0"
                        />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="circle"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Circle
                          size={16}
                          className="text-[rgb(var(--text-muted))] shrink-0"
                        />
                      </motion.span>
                    )}
                  </AnimatePresence>
                  <motion.span
                    className={`text-[12px] font-medium transition-colors duration-300 ${passed ? "text-[rgb(var(--success))]" : "text-[rgb(var(--text-secondary))]"}`}
                  >
                    {check.label}
                  </motion.span>
                </motion.div>
              );
            })}
          </div>

          {/* Pro tip */}
          <div className="mx-4 mb-4 p-4 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[rgb(var(--text-muted))] mb-1.5">
              Pro Tip
            </p>
            <p className="text-[12px] text-[rgb(var(--text-secondary))] leading-relaxed">
              Try a passphrase — four random words like{" "}
              <code className="text-[11px] bg-[rgb(var(--surface-3))] text-gold-2 px-1.5 py-0.5 rounded font-mono">
                river·ember·moss·14
              </code>{" "}
              are easier to remember and harder to crack.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
